import React from "react";
import { Link } from "react-router-dom";
import { authService, dashboardRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { useMockCustomer } from "./useMockCustomer";
import Icon from "../../components/ui/Icon";
import { Badge, Card, EmptyState, ProgressBar, Skeleton, StatusBadge, baht, formatDate, useAsync } from "../../components/ui";

export default function CustomerDashboard() {
  const { orgId, hasModule, businessModel } = useApp();
  const { tx } = useI18n();
  const profile = authService.currentProfile();
  const { customer } = useMockCustomer(orgId);
  const { data: summary, loading } = useAsync(() => (customer ? dashboardRepo.customerSummary(orgId, customer.id) : Promise.resolve(null)), [orgId, customer]);

  if (loading || !summary) return <Skeleton height={300} />;

  const activePackage = summary.packages?.[0];

  return (
    <>
      <Card style={{ background: "var(--gradient-brand)", color: "#fff", border: "none" }}>
        <div className="u-fs-13" style={{ opacity: 0.85 }}>{tx("สวัสดี", "Hello")}</div>
        <div className="u-fs-18 u-strong">{profile?.fullname || profile?.email}</div>
        <div className="u-fs-13 u-mt-8" style={{ opacity: 0.85 }}><Icon name={businessModel.icon} /> {tx("ยินดีต้อนรับสู่", "Welcome to")} {tx(businessModel.name, businessModel.nameEn)}</div>
      </Card>

      {hasModule("booking") && summary.nextAppointment ? (
        <Card title={tx("นัดหมายถัดไป", "Next appointment")}>
          <div className="u-strong u-fs-18">{summary.nextAppointment.serviceName}</div>
          <div className="u-fs-13 u-muted">{formatDate(summary.nextAppointment.startAt, true)}</div>
          <div className="u-mt-8"><StatusBadge status={summary.nextAppointment.status} /></div>
        </Card>
      ) : null}

      {hasModule("course") && activePackage ? (
        <Card title={tx("คอร์ส/แพ็กเกจของฉัน", "My package")}>
          <div className="u-strong">{activePackage.packageName}</div>
          <div className="u-fs-13 u-muted u-mb-8">{tx("ใช้ไป", "Used")} {activePackage.usedSessions}/{activePackage.totalSessions} {tx("ครั้ง", "sessions")}</div>
          <ProgressBar percent={(activePackage.usedSessions / activePackage.totalSessions) * 100} />
        </Card>
      ) : null}

      {summary.unpaidTotal > 0 ? (
        <Card title={tx("บิลค้างชำระ", "Outstanding bill")}>
          <div className="u-between">
            <span className="u-fs-13 u-muted">{tx("ยอดที่ต้องชำระ", "Amount due")}</span>
            <span className="u-strong u-danger u-fs-18">{baht(summary.unpaidTotal)}</span>
          </div>
          <Link to="/app/billing" className="btn-crm btn-primary-crm btn-block-crm u-mt-16">{tx("ไปหน้าชำระเงิน", "Go to billing")}</Link>
        </Card>
      ) : null}

      {hasModule("membership") ? (
        <Card title={tx("แต้มสะสมของฉัน", "My points")}>
          <div className="u-between">
            <span className="u-fs-13 u-muted">{tx("แต้มคงเหลือ", "Points balance")}</span>
            <span className="u-strong u-fs-18 u-indigo">{summary.wallet?.points ?? 0} pts</span>
          </div>
          <Link to="/app/wallet" className="btn-crm btn-ghost-crm btn-block-crm u-mt-16">{tx("ดูกระเป๋าเงิน", "View wallet")}</Link>
        </Card>
      ) : null}

      <Card title={tx("กิจกรรมล่าสุด", "Recent activity")}>
        {summary.activities?.length ? (
          summary.activities.slice(0, 5).map((item) => (
            <div key={item.id} className="u-between" style={{ padding: "8px 0", borderBottom: "1px solid var(--c-slate-100)" }}>
              <div>
                <div className="u-fs-13 u-strong">{item.title}</div>
                <div className="u-fs-12 u-muted">{formatDate(item.createdAt, true)}</div>
              </div>
              {item.amount ? <Badge tone="indigo">{baht(item.amount, 0)}</Badge> : null}
            </div>
          ))
        ) : (
          <EmptyState text={tx("ยังไม่มีความเคลื่อนไหว", "No activity yet")} />
        )}
      </Card>
    </>
  );
}
